import { is, List } from "immutable";
import _ from "lodash";
import * as React from "react";
import * as BS from "react-bootstrap";
import { connect } from "react-redux";
import { Link, RouteComponentProps, withRouter } from "react-router-dom";
import * as redux from "redux";
import { TestEmailModal, PreviewModal } from "./components";
import * as Actions from "../../../actions";
import * as Comp from "../../../components";
import { Paths } from "../../../constants";
import * as M from "../../../models";
import { Api } from "../../../services/Api";
import { AppState } from "../../../store";
import { ReactHelper, Utility } from "../../../utils";
import { AbstractsViewSwitcher } from "../components";

const mapStateToProps = (state: AppState, ownProps: OwnProps | undefined): ConnectedState => ({
    ...Comp.Base.DefaultListViewStatePropsMap(state, ownProps),
    loadingTypes: state.adminAbstractType.loadingTypes,
    eventAbstractsUpdates: state.abstract.subscribedEventAbstracts,
    invitationUpdates: state.adminAbstractType.subscribedInvitationUpdates,
    types: state.adminAbstractType.types,
});

const mapDispatchToProps = (dispatch: redux.Dispatch<AppState>): ConnectedDispatch => ({
    ...Comp.Base.DefaultDispatchPropsMap(dispatch),
    loadTypes: (eventId: number) => dispatch(Actions.AdminAbstractType.loadTypes(eventId)),
    subscribeToEventAbstracts: (eventId: number) => dispatch(Actions.Abstract.subscribeToEventAbstracts(eventId)),
    subscribeToInvitationUpdates: (abstractTypeId: number) => dispatch(Actions.AdminAbstractType.subscribeToInvitationUpdates(abstractTypeId)),
    toggleTypesLoading: (loading: boolean) => dispatch(Actions.AdminAbstract.toggleLoadingTypes(loading)),
});

type OwnProps = RouteComponentProps<{}> & {
    eventId: number;
};

type ConnectedState = Comp.Base.DefaultListViewConnectedState & {
    loadingTypes: boolean;
    eventAbstractsUpdates: M.Generic.ResourceUpdated;
    invitationUpdates: List<M.Abstract.InvitationEmailStatusUpdate>;
    types: List<M.Abstract.AbstractTypeEdit>;
};

type ConnectedDispatch = Comp.Base.DefaultConnectedDispatch & {
    loadTypes: (eventId: number) => void;
    subscribeToEventAbstracts: (eventId: number) => void;
    subscribeToInvitationUpdates: (abstractTypeId: number) => void;
    toggleTypesLoading: (loading: boolean) => void;
};

interface OwnState {
    connected: boolean;
    sending: M.Generic.ItemLoadingState;
    statuses: List<M.Abstract.InvitationEmailStatus>;
}

type CombinedProps = ConnectedState & ConnectedDispatch & OwnProps;

class InvitationEmailsComponent extends Comp.Base.BaseComponent<CombinedProps, OwnState> {

    private _previewModal: PreviewModal | null = null;
    private _testEmailModal: TestEmailModal | null = null;

    public constructor(props: CombinedProps) {
        super(props);

        this.state = {
            connected: false,
            sending: {},
            statuses: List(),
        };
    }

    public async componentDidMount() {
        this._isMounted = true;
        const eventId = this.props.eventId;

        this.props.subscribeToEventAbstracts(this.props.eventId);
        this.props.loadTypes(eventId);
        this.loadInvitationStatus();
        this.subscribeToStatusUpdates();

        const types = this.props.types.filter((x) => x !== undefined && x.eventId === this.props.eventId);
        if (!types.size)
            this.props.toggleTypesLoading(true);
    }

    public componentDidUpdate(prevProps: Readonly<CombinedProps>) {
        if (!is(prevProps.abstractsWebsocket, this.props.abstractsWebsocket))
            this.subscribeToStatusUpdates();

        if (!is(prevProps.websocket, this.props.websocket))
            this.props.subscribeToEventAbstracts(this.props.eventId);

        if (!is(prevProps.eventAbstractsUpdates[this.props.eventId], this.props.eventAbstractsUpdates[this.props.eventId])) {
            this.loadInvitationStatus();
            this.props.loadTypes(this.props.eventId);
        }

        if (!is(prevProps.invitationUpdates, this.props.invitationUpdates))
            this.onSendingUpdateReceived();

        if (!is(prevProps.types, this.props.types))
            this.subscribeToStatusUpdates();
    }

    public async componentWillUnmount() {
        this._isMounted = false;
    }

    public render() {
        const loading = this.state.loading || this.props.loadingTypes;
        const actionsCommon = <AbstractsViewSwitcher eventId={this.props.eventId} view="Acceptance emails" />;

        const abstractListLink = this.lastPathIsFromListView()
            ? <a href="#" onClick={(e) => { ReactHelper.emptyClick(e); window.history.back(); }}>Abstracts</a>
            : <Link to={Paths.AdminAbstractList(this.props.eventId)}>Abstracts</Link>;

        const types = this.props.types.filter((x) => x !== undefined && x.eventId === this.props.eventId);

        return <Comp.Page.AdminPage actionsCommon={actionsCommon} title={"Abstracts"}>

            <Comp.Generic.LoadingSpinner show={loading} />

            <PreviewModal
                api={this.props.api}
                eventId={this.props.eventId}
                ref={(ref) => this._previewModal = ref}
            />

            <TestEmailModal
                api={this.props.api}
                eventId={this.props.eventId}
                onComplete={() => this.props.notifySuccess("Test email has been successfully sent.")}
                ref={(ref) => this._testEmailModal = ref}
            />

            {this.getErrorAlert()}

            <h1>{abstractListLink} &raquo; Acceptance notification emails</h1>
            <p>Sent only to recipients who have <strong>not</strong> responded to invitation and do not have a COI.</p>

            <table>
                <tbody>
                    {types
                        .sortBy((type: M.Abstract.AbstractTypeEdit) => type.shortName)
                        .map((type: M.Abstract.AbstractTypeEdit, i) => {
                        const status = this.state.statuses.find((x) => x !== undefined && x.abstractTypeId === type.id);
                        const invitationStatus = this.props.invitationUpdates.find((x) => x !== undefined && x.abstractTypeId === type.id);
                        const sending = this.state.sending[type.id];
                        const sendingDisplay = invitationStatus && !invitationStatus.done && (invitationStatus.total || 0) > 0
                            ? <span className="mute">({invitationStatus.sent} / {invitationStatus.total})</span>
                            : null;

                        return <tr key={i}>
                            <td>
                                {type.invitationEmailTemplate
                                    ? <a
                                        href="#"
                                        onClick={(e) => { ReactHelper.emptyClick(e); this.showPreviewModal(type, undefined); }}
                                    >
                                        {type.shortName}
                                    </a>
                                    : type.shortName}
                                {" "}
                                <span className="mute">(<Link to={Paths.AdminAbstractTypeEdit(type.eventId, type.id)}>Edit</Link>)</span>
                                <br />
                                <span className="mute">
                                    {type.lastInvitationEmailsSent
                                        ? Utility.utcToLocalAsMoment(type.lastInvitationEmailsSent).format("MMM D, YYYY [at] h:mm a")
                                        : "Not sent"}
                                </span>
                            </td>
                            <td>
                                {status ? status.recipients : <>&mdash;</>}
                                <br /><span className="mute">Recipients</span>
                            </td>
                            <td>
                                {status ? status.responses : <>&mdash;</>}
                                <br /><span className="mute">Responses</span>
                            </td>
                            <td>
                                {type.invitationEmailTemplate
                                    ? (sending
                                        ? <BS.Button bsStyle="default" disabled>
                                            <Comp.Generic.LoadingIcon /> Sending&hellip; {sendingDisplay}
                                        </BS.Button>
                                        : <BS.Button bsStyle="primary" disabled={status && status.recipients === 0} onClick={() => this.sendEmails(type.id)}>
                                            Send notification
                                        </BS.Button>)
                                    : null}
                            </td>
                            <td>
                                {type.invitationEmailTemplate
                                    ? <a
                                        href="#"
                                        onClick={(e) => { ReactHelper.emptyClick(e); this.showTestEmailModal(type); }}
                                    >
                                        Send a test
                                    </a>
                                    : null}
                            </td>
                            <td>
                                {type.invitationEmailTemplate
                                    ? <Link to={Paths.AdminEmailTemplateEdit(type.invitationEmailTemplate.id)}>Email template</Link>
                                    : <Link to={Paths.AdminAbstractTypeEdit(type.eventId, type.id)}>No template</Link>}
                            </td>
                        </tr>;
                    })}
                </tbody>
            </table>

        </Comp.Page.AdminPage>;
    }

    private lastPathIsFromListView() {
        return this.props.lastPath === `${Paths.AdminAbstracts.BasePath}/${this.props.eventId}`
            || (this.props.lastPath || "").indexOf(`${Paths.AdminAbstracts.BasePath}/${this.props.eventId}?`) === 0;
    }

    private async loadInvitationStatus() {
        const eventId = this.props.eventId;
        const result = await this.props.api.get(Api.endpoints.admin.abstract.invitationEmailStatus.withUrlParams({ eventId }));

        if (result.isSuccess && result.data) {
            await this.setStateAsync({
                ...this.state,
                statuses: List(result.data),
            });
        }
        else {
            this.setErrors(M.Result.Result.errorsOrMessage(result));
        }
    }

    private async onSendingUpdateReceived() {
        const sending = { ...this.state.sending };
        let doUpdate = false;
        this.props.invitationUpdates.forEach((x) => {
            if (!x)
                return;

           // const isSending = (x.total || 0) > 0 && (x.sent || 0) < (x.total || 0);
            // sending[x.abstractTypeId] = isSending;

            if (!!sending[x.abstractTypeId] && x.done) {
                sending[x.abstractTypeId] = false;
                doUpdate = true;
            }
        });

        if (doUpdate)
            await this.setStateAsync({ ...this.state, sending });
    }

    private async sendEmails(abstractTypeId: number) {
        await this.updateSending(abstractTypeId, true);

        const eventId = this.props.eventId;
        const result = await this.props.api.post(Api.endpoints.admin.abstract.sendInvitationEmails.withUrlParams({
            abstractTypeId,
            eventId,
        }));

        if (!this._isMounted)
            return;

        if (!result.isSuccess) {
            this.props.notifyError(M.Result.Result.errorsOrMessage(result).join("<br />"));
            await this.updateSending(abstractTypeId, false);
        }
    }

    private showPreviewModal(abstractType: M.Abstract.AbstractTypeEdit, abstractId: number | undefined) {
        if (!this._previewModal)
            return;

        this._previewModal.show(abstractType, abstractId);
    }

    private showTestEmailModal(abstractType: M.Abstract.AbstractTypeEdit) {
        if (!this._testEmailModal)
            return;

        this._testEmailModal.show(abstractType);
    }

    private subscribeToStatusUpdates() {
        this.props.types.forEach(async (t) => {
            if (!t)
                return;
            this.props.subscribeToInvitationUpdates(t.id);
        });
    }

    private async updateSending(abstractTypeId: number, isSending: boolean) {
        const sending = { ...this.state.sending };
        sending[abstractTypeId] = isSending;
        await this.setStateAsync({ ...this.state, sending });
    }
}

export const InvitationEmails: React.ComponentClass<OwnProps> = connect(mapStateToProps, mapDispatchToProps)(withRouter(InvitationEmailsComponent) as any);



// WEBPACK FOOTER //
// ./src/containers/adminAbstracts/invitationEmails/InvitationEmails.tsx